import { create } from "zustand";
import type { PreJoinChoices } from "../components/PreJoinView";
import { useCallVolumeStore } from "./callVolumeStore";
import type { CallEndReason } from "../lib/evictionTag";

const RESUME_STORAGE_KEY = "weco.call.resume";

/** Minimal, JSON-serializable subset of `ActiveCallSession` persisted across a
 *  hard reload — no `url`/`token` (LiveKit JWTs are short-lived and should be
 *  re-minted, not reused) and no `AudioContext` (not serializable). */
export interface ResumableCallSession {
  slug: string;
  roomId: string;
  roomName: string;
  choices: PreJoinChoices;
  joinedAt: number;
}

function persistResumableSession(session: ResumableCallSession): void {
  try {
    sessionStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // sessionStorage unavailable (private browsing / storage disabled) —
    // refresh-resume just won't work; the rest of the call is unaffected.
  }
}

/** Reads back the persisted resume record, or null if absent/malformed. */
export function readResumableSession(): ResumableCallSession | null {
  try {
    const raw = sessionStorage.getItem(RESUME_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ResumableCallSession> | null;
    if (
      !parsed ||
      typeof parsed.slug !== "string" ||
      typeof parsed.roomId !== "string" ||
      typeof parsed.roomName !== "string" ||
      typeof parsed.joinedAt !== "number" ||
      !parsed.choices
    ) {
      return null;
    }
    return parsed as ResumableCallSession;
  } catch {
    return null;
  }
}

/** Clears the persisted resume record — call once a resume attempt is known
 *  to be unrecoverable (denied, expired, room gone) so it isn't retried. */
export function clearResumableSession(): void {
  try {
    sessionStorage.removeItem(RESUME_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export interface ActiveCallSession {
  /** /call/<slug> URL segment — distinguishes this call from any other. */
  slug: string;
  /** Backend Room UUID — same as slug for current room layout but kept
   *  explicit because chat REST endpoints want the canonical id. */
  roomId: string;
  /** Display name of the room (for pill label + InCallView header). */
  roomName: string;
  /** LiveKit serverUrl, pinned at Join time. */
  url: string;
  /** LiveKit JWT, pinned at Join time. */
  token: string;
  /** Audio/video/username choices captured at Join time. */
  choices: PreJoinChoices;
  /** Epoch-ms timestamp of when the user clicked Join — for the duration ticker. */
  joinedAt: number;
  /** AudioContext created during the Join gesture. Passed to LiveKit's
   *  webAudioMix so it is initialised within a user gesture on iOS Chrome,
   *  where a lazily-created AudioContext is silently suspended and routes
   *  audio to the earpiece instead of the loudspeaker. */
  audioContext?: AudioContext;
  /** True when this session came from a waiting-room admission (#1916): until
   *  it connects, the host may put the participant back in the waiting list. */
  admittedFromLobby?: boolean;
}

/**
 * Auto-rejoin progress for the reconnect controller (#1178).
 *  - "idle"        — connected normally, no reconnect in flight
 *  - "reconnecting" — an auto-rejoin is underway (drives the
 *                     "reconnecting…" indicator). It has no end state: the
 *                     controller keeps trying until it connects or the user
 *                     leaves (#2223).
 */
export type ReconnectStatus = "idle" | "reconnecting";

interface CallSessionState {
  active: ActiveCallSession | null;
  /** Deliberate-leave intent. Set true by Leave/End/nav-away handlers
   *  BEFORE the disconnect is triggered, so the reconnect controller can
   *  tell a voluntary exit apart from a network drop and no-op the
   *  auto-rejoin. Mirrors MeetingRoom's synchronous `voluntaryRef` (#1172);
   *  a plain field read via getState() in the handler is synchronous too. */
  leaveIntent: boolean;
  /** Auto-rejoin status for the UI. */
  reconnectStatus: ReconnectStatus;
  /** 1-based index of the current auto-rejoin attempt (0 when idle). */
  reconnectAttempt: number;
  /** Why the last session ended, when the post-call screen needs to say so
   *  (#1918). Keyed by slug so a stale reason never leaks onto another
   *  room's post-call screen; reset on the next setActive. */
  lastEnd: { slug: string; reason: CallEndReason } | null;
  /** Fresh join: replaces the session and resets leave-intent + reconnect
   *  state so a later drop is read as involuntary and starts from attempt 0. */
  setActive: (next: ActiveCallSession) => void;
  /** Ends the session and resets reconnect bookkeeping. Deliberately does
   *  NOT reset `leaveIntent`: a deliberate leave sets the flag and then calls
   *  clear(), but the LiveKit disconnect it triggers fires asynchronously
   *  (after the <LiveKitRoom> unmounts) — so the flag must survive clear() for
   *  the disconnect handler to read it. It is reset on the next setActive.
   *  `endReason` is recorded as `lastEnd` for the post-call screen. */
  clear: (endReason?: CallEndReason | null) => void;
  /** Flags the imminent disconnect as a deliberate leave. */
  markLeaveIntent: () => void;
  /** Swaps in freshly-minted LiveKit creds mid-reconnect WITHOUT touching
   *  leave-intent or reconnect status (unlike setActive). Updating the token
   *  prop is what drives <LiveKitRoom> to open a new connection. */
  updateCreds: (url: string, token: string) => void;
  /** Bumped to ask the mounted room to connect again with the creds it
   *  already holds (#2223: a rejoin that reuses a still-valid token). */
  connectNonce: number;
  reconnectWithHeldCreds: () => void;
  /** Records the user's current mic/camera state into `choices` (#2223).
   *  <LiveKitRoom> publishes per `choices` on every connect, so without this
   *  a rejoin would undo a mute (or an unmute) made during the call. Also
   *  persisted, so a reload-resume keeps it too. */
  setMediaEnabled: (audioEnabled: boolean, videoEnabled: boolean) => void;
  /** Updates the reconnect status + attempt counter driving the UI. */
  setReconnect: (status: ReconnectStatus, attempt: number) => void;
  /** Slug whose session was ended because the host put this participant back
   *  in the waiting list (#1916). Read by RoomRoute to show the waiting room
   *  instead of the post-call screen; survives clear(), reset on setActive. */
  requeuedSlug: string | null;
  markRequeued: (slug: string | null) => void;
}

export const useCallSessionStore = create<CallSessionState>((set) => ({
  active: null,
  leaveIntent: false,
  reconnectStatus: "idle",
  reconnectAttempt: 0,
  lastEnd: null,
  requeuedSlug: null,
  setActive: (next) => {
    persistResumableSession({
      slug: next.slug,
      roomId: next.roomId,
      roomName: next.roomName,
      choices: next.choices,
      joinedAt: next.joinedAt,
    });
    // Every join starts at 100% output volume (#1722). Reconnects go through
    // updateCreds, not here, so a mid-call drop keeps the user's level.
    useCallVolumeStore.getState().reset();
    set({
      active: next,
      leaveIntent: false,
      reconnectStatus: "idle",
      reconnectAttempt: 0,
      lastEnd: null,
      requeuedSlug: null,
    });
  },
  clear: (endReason) => {
    clearResumableSession();
    useCallVolumeStore.getState().reset();
    set((s) => ({
      active: null,
      reconnectStatus: "idle",
      reconnectAttempt: 0,
      lastEnd: endReason && s.active ? { slug: s.active.slug, reason: endReason } : null,
    }));
  },
  markLeaveIntent: () => set({ leaveIntent: true }),
  updateCreds: (url, token) =>
    set((s) => (s.active ? { active: { ...s.active, url, token } } : {})),
  connectNonce: 0,
  reconnectWithHeldCreds: () => set((s) => ({ connectNonce: s.connectNonce + 1 })),
  setMediaEnabled: (audioEnabled, videoEnabled) =>
    set((s) => {
      const a = s.active;
      if (!a) return {};
      if (a.choices.audioEnabled === audioEnabled && a.choices.videoEnabled === videoEnabled) {
        return {};
      }
      const active = { ...a, choices: { ...a.choices, audioEnabled, videoEnabled } };
      persistResumableSession({
        slug: active.slug,
        roomId: active.roomId,
        roomName: active.roomName,
        choices: active.choices,
        joinedAt: active.joinedAt,
      });
      return { active };
    }),
  setReconnect: (status, attempt) =>
    set({ reconnectStatus: status, reconnectAttempt: attempt }),
  markRequeued: (slug) => set({ requeuedSlug: slug }),
}));
